import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Typography} from "@mui/material";
import {PublishedWithChangesOutlined} from "@mui/icons-material";
import EnterpriseObject from "../eoec/eo/EnterpriseObject";
import enterpriseObjectClassFactory from "../eoec/EnterpriseObjectClassFactory";
import ChangeOrderTableApplet from "../eoec/applet/impl/Foundation/ChangeOrderTableApplet";
import ObjectFilterSidebar from "../components/Foundation/ObjectFilterSidebar";
import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import EventBus from "../util/EventBus";
import {useSessionContext} from "../util/SessionContext";
import {availableModes} from "../util/VisibilityModes";
import {CHANGE_ORDER_STATES, OPEN_CHANGE_ORDER_STATES,
        loadChangeOrderStatistics} from "../util/changeOrderStatistics";
import {ObjectStatistics} from "../util/objectStatistics";
import {Blue3, Grey2, Grey6, Grey17} from "../theme/styles";

/**
 * The change order list.
 *
 * LAID OUT LIKE THE RECORD LIST, deliberately and after getting it wrong once. The first cut put the
 * lifecycle across the top as a tab row and argued in a comment that a five-value enum did not
 * justify a pane -- which is the same argument ObjectList already considered and rejected: its own
 * comment reads "the lifecycle tabs that used to sit opposite it were removed in favour of the
 * sidebar's Status filters". Two lists in one product filtering the same kind of column two
 * different ways is worse than either choice made twice.
 *
 * THE TAB ROW IS NOT GONE, IT IS THE RIGHT TABS NOW. Across the top belongs the VISIBILITY MODE --
 * Position, Organization, Management -- which is what a reader changes to widen what they can see.
 * Without it the persona switcher appeared to do nothing on this screen: the list is persona-scoped
 * server-side, but there was no control saying so and no re-query when the persona changed.
 *
 * OPEN IS THE DEFAULT and the default is the point of the page. A change order list is a working
 * queue; what somebody opens it for is the ones still moving. IMPLEMENTATION counts as open -- it is
 * signed but not finished, which is precisely a thing still moving.
 */

const tabStyle = (active: boolean): React.CSSProperties => ({
    cursor: "pointer",
    paddingBottom: 6,
    fontSize: 14,
    fontWeight: active ? 600 : 400,
    color: active ? Grey17 : Grey6,
    borderBottom: active ? `2px solid ${Blue3}` : "2px solid transparent",
});

export default function ChangeOrderList() {
    const sessionContext = useSessionContext();
    const [selectedStates, setSelectedStates] =
        useState<Set<string>>(new Set(OPEN_CHANGE_ORDER_STATES));
    const [statistics, setStatistics] = useState<ObjectStatistics | null>(null);

    // Built once. Rebuilding it on every render would hand the grid a fresh component each time and
    // it would re-query forever.
    const changeOrdersEO: EnterpriseObject | undefined = useMemo(() => {
        const eo = enterpriseObjectClassFactory("ChangeOrder");
        // FILTERED BEFORE THE GRID MOUNTS. PlatformTable runs its first query during mount, ahead of
        // any effect here, so a filter applied in a useEffect would let that first load through
        // unfiltered and then visibly re-query.
        (eo?.getEnterpriseComponent("ChangeOrder") as any)
            ?.setStatesFilter?.(OPEN_CHANGE_ORDER_STATES);
        return eo;
    }, []);

    const refreshStatistics = useCallback(() => {
        loadChangeOrderStatistics(sessionContext).then(setStatistics);
    }, [sessionContext]);

    useEffect(() => { refreshStatistics(); }, [refreshStatistics]);

    // RE-QUERY AND RECOUNT ON EVERY PERSONA CHANGE. Visibility decides which change orders resolve
    // at all, so counts taken under the previous persona would be wrong -- and without this the
    // switcher appeared to do nothing here until the browser was reloaded, which is exactly how it
    // was reported. ObjectList and ProjectPlan have carried this listener all along.
    useEffect(() => {
        const onChangeVisibility = () => {
            (changeOrdersEO?.getEnterpriseComponent("ChangeOrder") as any)?.invalidate?.();
            refreshStatistics();
        };
        EventBus.visibility.addListener("change-visibility", onChangeVisibility);
        return () => {
            EventBus.visibility.removeListener("change-visibility", onChangeVisibility);
        };
    }, [changeOrdersEO, refreshStatistics]);

    const applyStates = useCallback((states: Set<string>) => {
        const ec: any = changeOrdersEO?.getEnterpriseComponent("ChangeOrder");
        // NO STATES TICKED MEANS NO FILTER, not "match nothing". Unticking everything is how a
        // reader asks for the whole list, and answering it with an empty grid reads as a fault.
        ec?.setStatesFilter?.(Array.from(states));
        ec?.invalidate?.();
    }, [changeOrdersEO]);

    const toggleState = (state: string) => {
        const next = new Set(selectedStates);
        if (next.has(state)) next.delete(state);
        else next.add(state);
        setSelectedStates(next);
        applyStates(next);
    };

    const clearFilters = () => {
        const next = new Set<string>();
        setSelectedStates(next);
        applyStates(next);
    };

    const activeMode = sessionContext?.getActiveVisibility()?.Mode ?? "";
    const visibleModes = availableModes(activeMode, sessionContext.getPrivileges(), sessionContext.getAccount());

    const selectMode = (mode: string) => {
        const current = sessionContext?.getActiveVisibility();
        sessionContext?.setActiveVisibility(mode, current?.Value ?? "");
        EventBus.visibility.emit("change-visibility", sessionContext?.getActiveVisibility());
    };

    return (
        // height 100%, NOT JUST flexGrow. The shell hands this page a fixed box that scrolls, and a
        // block parent lets a flex child size to its content rather than fill it -- which leaves the
        // sidebar's separator painting to the height of the TABLE instead of the window.
        <Box sx={{display: "flex", flexDirection: "column", height: "100%", minWidth: 0}}>
            <PageHeader title="Change Orders">
                <PersonaHeaderInfo/>
            </PageHeader>

            <div style={{display: "flex", flexDirection: "row", flexGrow: 1, minHeight: 0}}>
                <ObjectFilterSidebar
                    selectedStatuses={selectedStates}
                    onToggleStatus={toggleState}
                    statuses={CHANGE_ORDER_STATES}
                    statusHeading="State"
                    // NO RECENCY HERE. A change order list's useful axis is the lifecycle; "days
                    // old" would be a second control nobody moves, on a list whose rows are mostly
                    // days old anyway.
                    showRecency={false}
                    storageKey="ChangeOrderFilterSidebar"
                    newestOnly={false}
                    onToggleNewest={() => undefined}
                    daysOldEnabled={false}
                    onToggleDaysOld={() => undefined}
                    daysOld=""
                    onDaysOldChange={() => undefined}
                    statistics={statistics}
                    onClear={clearFilters}
                />

                <div style={{flex: 1, overflow: "auto", padding: "0 30px", display: "flex", flexDirection: "column", minHeight: 0}}>
                    <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #E4E7EC"}}>
                        <div style={{display: "flex", flexDirection: "row", alignItems: "center", gap: 12}}>
                            <Box sx={{width: 34, height: 34, borderRadius: "6px", backgroundColor: "rgba(20,152,216,0.12)", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                <PublishedWithChangesOutlined sx={{color: Blue3, fontSize: 20}}/>
                            </Box>
                            <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>Change Orders</Typography>
                        </div>
                    </div>

                    {/* The visibility-mode selector -- what the persona switcher's effect on this
                        list is actually called. Empty when the caller has one persona and no
                        widening grant, because a lone tab describes the only view they will ever
                        get and reads as something broken. */}
                    <div style={{display: "flex", flexDirection: "row", alignItems: "flex-end", borderBottom: `1px solid ${Grey2}`, marginTop: 16}}>
                        <div style={{display: "flex", gap: 22}}>
                            {visibleModes.map(m => (
                                <div key={m.mode} onClick={() => selectMode(m.mode)} style={tabStyle(activeMode === m.mode)}>
                                    {m.label}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div style={{margin: "30px 0", display: "flex", flexDirection: "column"}}>
                        <ChangeOrderTableApplet
                            enterpriseComponent={changeOrdersEO?.getEnterpriseComponent("ChangeOrder")}/>
                    </div>
                </div>
            </div>
        </Box>
    );
}
